/**
 * Title Folder Audit Dashboard
 *
 * Main overview page for the Title Folder Management & Archival System.
 * Displays audit statistics, recent findings, and quick actions.
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Play,
  RefreshCw,
  FolderSearch,
  FolderX,
  FolderCheck,
  FolderArchive,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Layers,
  Archive,
  FileQuestion,
  Zap,
} from "lucide-react";
import {
  runTitleFolderAudit,
  searchTitleFolders,
  getOrphanFolders,
  formatRelativeTime,
  type AuditRunResult,
  type TitleFolderIndexRecord,
  type OrphanFolderRecord,
} from "@/lib/title-folder-api";
import { TitleFolderLayout } from "./layout";

interface DashboardStats {
  totalIndexed: number;
  inSharePoint: number;
  archived: number;
  missing: number;
  restoring: number;
  valid: number;
  namingMismatch: number;
  wrongBucket: number;
  orphanCount: number;
  lastAuditedUtc: string | null;
}

export default function TitleFolderDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentMissing, setRecentMissing] = useState<TitleFolderIndexRecord[]>([]);
  const [recentOrphans, setRecentOrphans] = useState<OrphanFolderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningAudit, setRunningAudit] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditConfig, setAuditConfig] = useState({
    siteId: "",
    driveId: "",
    dryRun: true,
  });
  const { toast } = useToast();

  const loadDashboard = async () => {
    try {
      setLoading(true);

      // Load stats in parallel
      const [
        allFolders,
        inSharePointRes,
        archivedRes,
        missingRes,
        restoringRes,
        validRes,
        namingMismatchRes,
        wrongBucketRes,
        orphansRes,
      ] = await Promise.all([
        searchTitleFolders({ limit: 1 }),
        searchTitleFolders({ locationState: 'InSharePoint', limit: 1 }),
        searchTitleFolders({ locationState: 'Archived', limit: 1 }),
        searchTitleFolders({ locationState: 'Missing', limit: 5 }),
        searchTitleFolders({ locationState: 'Restoring', limit: 1 }),
        searchTitleFolders({ auditStatus: 'Valid', limit: 1 }),
        searchTitleFolders({ auditStatus: 'NamingMismatch', limit: 1 }),
        searchTitleFolders({ auditStatus: 'WrongBucket', limit: 1 }),
        getOrphanFolders({ limit: 5 }),
      ]);

      setStats({
        totalIndexed: allFolders.totalCount,
        inSharePoint: inSharePointRes.totalCount,
        archived: archivedRes.totalCount,
        missing: missingRes.totalCount,
        restoring: restoringRes.totalCount,
        valid: validRes.totalCount,
        namingMismatch: namingMismatchRes.totalCount,
        wrongBucket: wrongBucketRes.totalCount,
        orphanCount: orphansRes.totalCount,
        lastAuditedUtc: allFolders.rows[0]?.lastAuditedUtc ?? null,
      });

      setRecentMissing(missingRes.rows);
      setRecentOrphans(orphansRes.rows);
    } catch (error: any) {
      toast({
        title: "Error loading dashboard",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const handleRunAudit = async () => {
    if (!auditConfig.siteId || !auditConfig.driveId) {
      toast({
        title: "Configuration required",
        description: "Please enter SharePoint Site ID and Drive ID",
        variant: "destructive",
      });
      return;
    }

    try {
      setRunningAudit(true);
      setAuditDialogOpen(false);

      const result = await runTitleFolderAudit({
        siteId: auditConfig.siteId,
        driveId: auditConfig.driveId,
        dryRun: auditConfig.dryRun,
      });

      toast({
        title: auditConfig.dryRun ? "Dry Run Complete" : "Audit Complete",
        description: `Found ${result.summary.matchedCorrectly} matched, ${result.summary.missingFolders} missing, ${result.summary.orphanFolders} orphans`,
      });

      if (!auditConfig.dryRun) {
        loadDashboard();
      }
    } catch (error: any) {
      toast({
        title: "Audit failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setRunningAudit(false);
    }
  };

  if (loading) {
    return (
      <TitleFolderLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-200 dark:border-amber-900"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FolderSearch className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="mt-4 text-sm font-medium text-slate-600 dark:text-slate-400 tracking-wide">
              Loading audit data...
            </p>
          </div>
        </div>
      </TitleFolderLayout>
    );
  }

  const totalIssues = (stats?.missing ?? 0) + (stats?.namingMismatch ?? 0) + (stats?.wrongBucket ?? 0) + (stats?.orphanCount ?? 0);
  const healthPercentage = stats?.totalIndexed ? Math.round(((stats.totalIndexed - totalIssues) / stats.totalIndexed) * 100) : 0;

  return (
    <TitleFolderLayout onRefresh={loadDashboard} isRefreshing={loading}>
      <div className="p-6 space-y-8">
        {/* Header Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-amber-950/50 dark:via-orange-950/50 dark:to-yellow-950/50 border border-amber-200/50 dark:border-amber-800/30 p-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-200/20 to-orange-200/20 dark:from-amber-800/10 dark:to-orange-800/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="relative flex justify-between items-start">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/50 ring-1 ring-amber-200 dark:ring-amber-800">
                  <Layers className="h-6 w-6 text-amber-700 dark:text-amber-300" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  Title Folder Audit
                </h1>
              </div>
              <p className="text-slate-600 dark:text-slate-400 max-w-xl">
                Correlate Dataverse titles with SharePoint folders. Identify missing folders,
                orphans, and misplacements across your document library structure.
              </p>
              {stats?.lastAuditedUtc && (
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-500">
                  <Clock className="h-4 w-4" />
                  <span>Last audit: {formatRelativeTime(stats.lastAuditedUtc)}</span>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={loadDashboard} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-600/25">
                    <Play className="h-4 w-4 mr-2" />
                    Run Audit
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-amber-600" />
                      Run Title Folder Audit
                    </DialogTitle>
                    <DialogDescription>
                      Configure and run an audit to compare Dataverse titles against SharePoint folders.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="siteId">SharePoint Site ID</Label>
                      <Input
                        id="siteId"
                        placeholder="e.g., contoso.sharepoint.com,guid,guid"
                        value={auditConfig.siteId}
                        onChange={(e) => setAuditConfig({ ...auditConfig, siteId: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="driveId">Drive ID</Label>
                      <Input
                        id="driveId"
                        placeholder="e.g., b!abc123..."
                        value={auditConfig.driveId}
                        onChange={(e) => setAuditConfig({ ...auditConfig, driveId: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="dryRun"
                        checked={auditConfig.dryRun}
                        onCheckedChange={(checked) =>
                          setAuditConfig({ ...auditConfig, dryRun: checked as boolean })
                        }
                      />
                      <Label htmlFor="dryRun" className="text-sm text-slate-600 dark:text-slate-400">
                        Dry run (don't persist results)
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAuditDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleRunAudit} disabled={runningAudit}>
                      {runningAudit ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Start Audit
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Health Score & Summary Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Health Score Card */}
          <Card className="lg:row-span-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                Audit Health
              </CardTitle>
              <CardDescription>
                Overall folder correlation status
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="flex flex-col items-center">
                <div className="relative w-40 h-40">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-slate-100 dark:text-slate-800"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${healthPercentage * 4.4} 440`}
                      className={`transition-all duration-1000 ${
                        healthPercentage >= 80 ? 'text-emerald-500' :
                        healthPercentage >= 50 ? 'text-amber-500' :
                        'text-red-500'
                      }`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-slate-800 dark:text-slate-200">
                      {healthPercentage}%
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Healthy
                    </span>
                  </div>
                </div>
                <div className="mt-6 w-full space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Total Indexed</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {stats?.totalIndexed ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Issues Found</span>
                    <span className="font-semibold text-amber-600 dark:text-amber-400">
                      {totalIssues}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location State Cards */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">In SharePoint</p>
                  <p className="text-3xl font-bold text-slate-800 dark:text-slate-200 mt-1">
                    {stats?.inSharePoint ?? 0}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/50">
                  <FolderCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <div className="mt-4">
                <Progress
                  value={stats?.totalIndexed ? (stats.inSharePoint / stats.totalIndexed) * 100 : 0}
                  className="h-1.5 bg-slate-100 dark:bg-slate-800"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Archived</p>
                  <p className="text-3xl font-bold text-slate-800 dark:text-slate-200 mt-1">
                    {stats?.archived ?? 0}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/50">
                  <Archive className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="mt-4">
                <Progress
                  value={stats?.totalIndexed ? (stats.archived / stats.totalIndexed) * 100 : 0}
                  className="h-1.5 bg-slate-100 dark:bg-slate-800"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Missing</p>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-1">
                    {stats?.missing ?? 0}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/50">
                  <FolderX className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="mt-4">
                <Progress
                  value={stats?.totalIndexed ? (stats.missing / stats.totalIndexed) * 100 : 0}
                  className="h-1.5 bg-slate-100 dark:bg-slate-800"
                />
              </div>
            </CardContent>
          </Card>

          {/* Issues Breakdown */}
          <Card className="lg:col-span-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Issues Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="flex-1 text-sm text-slate-600 dark:text-slate-400">Missing Folders</span>
                <Badge variant="destructive">{stats?.missing ?? 0}</Badge>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="flex-1 text-sm text-slate-600 dark:text-slate-400">Orphan Folders</span>
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                  {stats?.orphanCount ?? 0}
                </Badge>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="flex-1 text-sm text-slate-600 dark:text-slate-400">Wrong Bucket</span>
                <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                  {stats?.wrongBucket ?? 0}
                </Badge>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="flex-1 text-sm text-slate-600 dark:text-slate-400">Naming Mismatch</span>
                <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
                  {stats?.namingMismatch ?? 0}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Findings Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Missing Folders */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <FolderX className="h-5 w-5 text-red-500" />
                  Missing Folders
                </CardTitle>
                <CardDescription>Titles without SharePoint folders</CardDescription>
              </div>
              <Link href="/title-folders/browse?locationState=Missing">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-800">
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentMissing.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
                  <p>No missing folders found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentMissing.map((folder) => (
                    <div
                      key={folder.titleId}
                      className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <FolderX className="h-5 w-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                          {folder.projectNumber ?? 'No Project #'} - {folder.titleName ?? 'Unknown Title'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          Expected: {folder.expectedFolderName ?? 'Unknown'} in {folder.expectedBucket ?? 'Unknown bucket'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Orphan Folders */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <FileQuestion className="h-5 w-5 text-amber-500" />
                  Orphan Folders
                </CardTitle>
                <CardDescription>SharePoint folders without matching titles</CardDescription>
              </div>
              <Link href="/title-folders/orphans">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-800">
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentOrphans.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
                  <p>No orphan folders found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentOrphans.map((orphan) => (
                    <div
                      key={orphan.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <FileQuestion className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                          {orphan.folderName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {orphan.bucketFolder ?? 'Unknown bucket'}
                          {orphan.parsedProjectNumber && ` - Parsed: ${orphan.parsedProjectNumber}`}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          orphan.resolutionStatus === 'Linked'
                            ? 'border-emerald-300 text-emerald-600'
                            : 'border-slate-300 text-slate-600'
                        }
                      >
                        {orphan.resolutionStatus ?? 'Unresolved'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Empty State */}
        {stats?.totalIndexed === 0 && (
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/50 mb-6">
                <FolderSearch className="h-12 w-12 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">
                No Audit Data Yet
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-center max-w-md mb-6">
                Run your first title folder audit to compare Dataverse titles against
                your SharePoint folder structure.
              </p>
              <Button
                onClick={() => setAuditDialogOpen(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Play className="h-4 w-4 mr-2" />
                Run First Audit
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </TitleFolderLayout>
  );
}
